import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container } from '@/components/layout/Container';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { useAuthStore } from '@/store/authStore';
import { FadeIn, StaggerContainer, StaggerItem } from '@/components/ui/AnimatedCard';
import {
  Target,
  ListTodo,
  Calendar,
  BarChart3,
  Sparkles,
  Shield,
  ArrowRight,
  CheckCircle2,
  Zap,
} from 'lucide-react';
import { Logo } from '@/components/ui/Logo';

// Feature definitions
const features = [
  {
    icon: Target,
    title: 'Aspirations & Goals',
    description: 'Define your life vision and break it down into achievable SMART goals with clear deadlines and progress tracking.'
  },
  {
    icon: ListTodo,
    title: 'Smart Lists',
    description: 'Organize tasks into hierarchical lists linked to your goals. Reusable templates save time on recurring projects.'
  },
  {
    icon: Calendar,
    title: 'Calendar Integration',
    description: 'Schedule your work with an intelligent calendar that helps you allocate time across all your aspirations.'
  },
  {
    icon: BarChart3,
    title: 'Progress Insights',
    description: 'Track your journey with visual projections, completion metrics, and detailed reports on your achievements.'
  },
  {
    icon: Sparkles,
    title: 'AI-Powered Assistance',
    description: 'Get smart suggestions for task scheduling, goal optimization, and maintaining balance across life areas.'
  },
  {
    icon: Shield,
    title: 'Cloud Sync & Backup',
    description: 'Your data is securely stored and synchronized across all your devices. Never lose your progress.'
  },
];

// Benefits list
const benefits = [
  'Turn big dreams into daily actions',
  'Stay focused on what truly matters',
  'Track progress across all life areas',
  'Build consistent habits that stick',
  'Achieve more with less overwhelm',
];

// How it works steps
const howItWorks = [
  {
    step: 1,
    title: 'Define Your Vision',
    description: 'Start with your core aspirations - the big picture of who you want to become and what you want to achieve.'
  },
  {
    step: 2,
    title: 'Set SMART Goals',
    description: 'Break each aspiration into specific, measurable, achievable, relevant, and time-bound goals.'
  },
  {
    step: 3,
    title: 'Create Action Lists',
    description: 'Transform goals into actionable task lists with priorities, deadlines, and time estimates.'
  },
  {
    step: 4,
    title: 'Track & Achieve',
    description: 'Work through your tasks, monitor progress, and celebrate as you achieve your goals.'
  },
];

export function Landing() {
  const { setShowAuthModal, setAuthModalMode } = useAuthStore();
  const [pioneerSpots, setPioneerSpots] = useState<number | null>(null);

  useEffect(() => {
    // Fetch pioneer count on load
    const fetchPioneerCount = async () => {
      try {
        const API_BASE = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${API_BASE}/api/pioneer-count`);
        if (response.ok) {
          const data = await response.json();
          setPioneerSpots(data.spotsRemaining);
        }
      } catch (error) {
        console.warn('Could not fetch pioneer count:', error);
      }
    };
    fetchPioneerCount();
  }, []);

  const handleSignUp = () => {
    setAuthModalMode('signup');
    setShowAuthModal(true);
  };

  const handleSignIn = () => {
    setAuthModalMode('signin');
    setShowAuthModal(true);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-primary/5 to-transparent">
        <Container size="lg">
          <div className="text-center max-w-4xl mx-auto">
            <FadeIn>
              <div className="flex justify-center mb-6">
                <Logo size="xl" className="md:w-24 md:h-24" />
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tight">
                Transform Your Dreams Into{' '}
                <span className="text-primary">Reality</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                GodMode is the hierarchical productivity system that connects your life vision
                to daily actions. From aspirations to tasks, every action moves you forward.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" onClick={handleSignUp} animation="scale">
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" onClick={handleSignIn}>
                  Sign In
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                No credit card required. Free plan available.
              </p>
            </FadeIn>
          </div>
        </Container>
      </section>

      {/* Benefits Bar */}
      <section className="py-8 bg-surface-1 border-y">
        <Container size="lg">
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2">
            {benefits.map((benefit) => (
              <div key={benefit} className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24">
        <Container size="lg">
          <FadeIn>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                The Four-Level System
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A proven framework that bridges the gap between your biggest dreams and today's to-do list.
              </p>
            </div>
          </FadeIn>

          <StaggerContainer>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {howItWorks.map((item) => (
                <StaggerItem key={item.step}>
                  <Card className="h-full text-center">
                    <CardContent className="pt-6">
                      <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold mx-auto mb-4">
                        {item.step}
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {item.description}
                      </p>
                    </CardContent>
                  </Card>
                </StaggerItem>
              ))}
            </div>
          </StaggerContainer>

          {/* Hierarchy Visual */}
          <div className="mt-12 p-6 bg-surface-1 rounded-lg border">
            <div className="font-mono text-sm text-center space-y-1">
              <div className="text-primary font-semibold">ASPIRATIONS (Life Vision)</div>
              <div className="text-muted-foreground">&darr;</div>
              <div className="text-primary font-semibold">GOALS (SMART Objectives)</div>
              <div className="text-muted-foreground">&darr;</div>
              <div className="text-primary font-semibold">LISTS (Project Collections)</div>
              <div className="text-muted-foreground">&darr;</div>
              <div className="text-primary font-semibold">ITEMS (Individual Tasks)</div>
            </div>
          </div>
        </Container>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-surface-1">
        <Container size="lg">
          <FadeIn>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Everything You Need to Succeed
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Powerful features designed to help you define, plan, execute, and achieve your most important goals.
              </p>
            </div>
          </FadeIn>

          <StaggerContainer>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <StaggerItem key={feature.title}>
                    <Card className="h-full">
                      <CardContent className="pt-6">
                        <div className="rounded-full bg-primary/10 w-12 h-12 flex items-center justify-center mb-4">
                          <Icon className="h-6 w-6 text-primary" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {feature.description}
                        </p>
                      </CardContent>
                    </Card>
                  </StaggerItem>
                );
              })}
            </div>
          </StaggerContainer>
        </Container>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 md:py-24">
        <Container size="lg">
          <FadeIn>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Join Achievers Who Get Things Done
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                GodMode helps people across industries turn their visions into measurable results.
              </p>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-primary mb-2">4 Levels</div>
              <div className="text-muted-foreground">From vision to action</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary mb-2">SMART</div>
              <div className="text-muted-foreground">Goal framework built-in</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary mb-2">24/7</div>
              <div className="text-muted-foreground">Cloud sync & access</div>
            </div>
          </div>
        </Container>
      </section>

      {/* Pricing Teaser */}
      <section className="py-16 md:py-24 bg-surface-1">
        <Container size="lg">
          <div className="max-w-3xl mx-auto">
            <FadeIn>
              <Card className="border-primary">
                <CardContent className="p-8 md:p-12 text-center">
                  <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-medium mb-6">
                    <Zap className="h-4 w-4" />
                    Start for Free
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                    Ready to Achieve Your Dreams?
                  </h2>
                  <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
                    Start with our free plan and upgrade to Pro when you're ready for
                    unlimited aspirations, AI insights, and advanced features.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button size="lg" onClick={handleSignUp} animation="scale">
                      Create Free Account
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Link to="/help">
                      <Button size="lg" variant="outline">
                        Learn More
                      </Button>
                    </Link>
                  </div>
                  <p className="text-sm text-muted-foreground mt-6">
                    Pro plan starts at <span className="font-semibold text-foreground">£3.99/month</span> with 14-day free trial
                  </p>
                  {pioneerSpots !== null && pioneerSpots > 0 ? (
                    <p className="text-xs text-primary mt-2 font-medium animate-pulse">
                      🚀 Only <span className="font-bold text-lg">{pioneerSpots}</span> of 10 Pioneer spots left — FREE lifetime Pro access!
                    </p>
                  ) : pioneerSpots === 0 ? (
                    <p className="text-xs text-muted-foreground mt-2">
                      All Pioneer spots claimed! 14-day free trial still available.
                    </p>
                  ) : (
                    <p className="text-xs text-primary mt-2 font-medium animate-pulse">
                      🚀 First 10 Pioneers get FREE lifetime Pro access — limited spots!
                    </p>
                  )}
                </CardContent>
              </Card>
            </FadeIn>
          </div>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24">
        <Container size="lg">
          <div className="text-center">
            <FadeIn>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Start Your Journey Today
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
                Join the community of achievers who use GodMode to turn their aspirations into accomplishments.
              </p>
              <Button size="lg" onClick={handleSignUp} animation="scale">
                Get Started for Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </FadeIn>
          </div>
        </Container>
      </section>

      {/* Footer Links */}
      <footer className="py-8 border-t">
        <Container size="lg">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Logo size="md" />
              <span className="font-semibold">GodMode</span>
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
              <Link to="/help" className="hover:text-foreground transition-colors">Help</Link>
              <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            </div>
            <div className="text-sm text-muted-foreground">
              &copy; {new Date().getFullYear()} GodMode. All rights reserved.
            </div>
          </div>
        </Container>
      </footer>
    </div>
  );
}
